import React, { useState } from 'react'
import { toast } from 'react-hot-toast'
import { animeDBService } from '../../services/AnimeDB/animeDB.service.remote'
import { watchOrReadListService } from '../../services/UserList'
import { images } from '../../services/images.service'
import { getAnimeImage } from '../../services/util.service'

export function MALImport({ userId }) {
  const [file, setFile] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  // פונקציה שמעדכנת את הקובץ שנבחר
  const handleFileChange = (event) => {
    setFile(event.target.files[0])
  }

  // פונקציה שמעבדת תאריך ומחזירה מחרוזת בפורמט ישראלי (DD/MM/YYYY)
  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return null
    const dateObj = new Date(dateString)
    return new Intl.DateTimeFormat('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'Asia/Jerusalem',
    }).format(dateObj)
  }

  const handleImport = async () => {
    if (!file) {
      toast.error('אנא בחר קובץ XML שהורדת מ-MyAnimeList.')
      return
    }

    if (!userId) {
      toast.error('משתמש אינו מזוהה. אנא התחבר מחדש.')
      return
    }

    setIsLoading(true)

    try {
      let fileContent = ''

      if (file.name.endsWith('.gz')) {
        // שימוש ב-DecompressionStream לפענוח קבצי GZip (נתמך בדפדפנים מודרניים)
        if (typeof DecompressionStream === 'undefined') {
          toast.error('דפדפן זה אינו תומך בפענוח קבצי GZip ללא ספרייה נוספת.')
          setIsLoading(false)
          return
        }
        const ds = new DecompressionStream('gzip')
        const decompressedStream = file.stream().pipeThrough(ds)
        fileContent = await new Response(decompressedStream).text()
      } else {
        fileContent = await file.text()
      }

      // ניתוח קובץ ה-XML
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(fileContent, 'text/xml')

      const animeNodes = xmlDoc.getElementsByTagName('anime')
      const animeList = []

      for (let i = 0; i < animeNodes.length; i++) {
        const anime = animeNodes[i]
        const mal_id =
          anime.getElementsByTagName('series_animedb_id')[0]?.textContent

        // קבלת ערכי תאריכים וגיבושם באמצעות formatDate
        const rawStart =
          anime.getElementsByTagName('my_start_date')[0]?.textContent
        const rawFinish =
          anime.getElementsByTagName('my_finish_date')[0]?.textContent
        const start_date = formatDate(rawStart)
        const finish_date = formatDate(rawFinish)

        const status =
          anime.getElementsByTagName('my_status')[0]?.textContent ||
          'Plan to Watch'
        const progress = parseInt(
          anime.getElementsByTagName('my_watched_episodes')[0]?.textContent ||
            '0',
          10
        )
        const episodes = parseInt(
          anime.getElementsByTagName('series_episodes')[0]?.textContent || '0',
          10
        )

        if (mal_id) {
          animeList.push({
            mal_id,
            status,
            progress,
            start_date,
            finish_date,
          })
        }
      }

      if (animeList.length === 0) {
        toast.error('לא נמצאו נתונים תקינים בקובץ.')
        setIsLoading(false)
        return
      }

      // שליפת האנימות הקיימות במסד הנתונים לפי mal_id
      const existingAnimes = await animeDBService.getByIds(
        animeList.map((a) => a.mal_id)
      )
      const existingMalIds = new Set(
        existingAnimes.map((anime) => anime.mal_id)
      )

      // סינון רק האנימות שקיימות במערכת שלך
      const filteredList = animeList.filter((anime) =>
        existingMalIds.has(parseInt(anime.mal_id))
      )

      if (filteredList.length === 0) {
        toast.error('לא נמצאו אנימות שתואמות למסד הנתונים שלך.')
        setIsLoading(false)
        return
      }

      // הכנת האובייקטים לפי המבנה הרצוי
      for (const anime of filteredList) {
        const matchingAnime = existingAnimes.find(
          (a) => a.mal_id === parseInt(anime.mal_id)
        )
        if (!matchingAnime) continue

        const animeToSave = {
          anizone_id: matchingAnime.mal_id, // מזהה פנימי
          mal_id: matchingAnime.mal_id,
          title_english: matchingAnime.title_english || matchingAnime.title,
          title_hebrew: matchingAnime.title_hebrew || '',
          image_url: getAnimeImage(matchingAnime),
          progress: anime.progress,
          status: anime.status,
          episodes: anime.episodes,
          start_date:
            anime.start_date ||
            new Intl.DateTimeFormat('he-IL', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              timeZone: 'Asia/Jerusalem',
            }).format(new Date()),
          finish_date: anime.finish_date,
          userId: userId, // וידוא שהמשתמש מועבר
        }

        await watchOrReadListService.save(userId, 'watchlist', animeToSave)
      }

      toast.success('הרשימה יובאה בהצלחה!')
    } catch (err) {
      console.error('❌ שגיאה בייבוא הרשימה:', err)
      toast.error('אירעה שגיאה בייבוא הרשימה.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div
        className="max-w-lg mx-auto p-8 bg-gradient-to-br bg-secondaryBackground text-white rounded-xl mt-4 border"
        style={{
          borderBottomWidth: '2px', // עובי הגבול
          borderBottomStyle: 'solid', // חובה להגדיר סגנון גבול
          borderImageSource: `url(${images.redBorder})`, // מקור תמונת הגבול
          borderImageSlice: '20', // קובע חיתוך לתמונה
          borderImageRepeat: 'stretch', // למתוח את התמונה כך שתמלא את הגבול
        }}
      >
        <h2 className="text-3xl font-bold mb-6 text-center">
          ייבוא רשימת צפייה מ- MyAnimeList \ AniList
        </h2>

        {/* אזור בחירת קובץ */}
        <label
          htmlFor="file-upload"
          className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-primary transition-colors"
        >
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            <svg
              className="w-10 h-10 mb-3 text-primary"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V9.414a2 2 0 00-.586-1.414l-4.414-4.414A2 2 0 0011.586 3H4zm0 2h7v4a1 1 0 001 1h4v6H4V5z" />
            </svg>
            <p className="mb-2 text-sm text-gray-300">
              גרור ושחרר את הקובץ או בחר קובץ
            </p>
            <p className="text-xs text-gray-400">תומך בקבצי .xml ו-.gz</p>
          </div>
          <input
            id="file-upload"
            type="file"
            accept=".xml,.gz"
            onChange={handleFileChange}
            className="hidden"
          />
        </label>

        {/* הצגת הקובץ הנבחר עם אפשרות להסרה */}
        {file && (
          <div className="mt-4 flex text-center gap-4 items-center justify-center text-gray-300">
            קובץ נבחר: {file.name}
            <button
              onClick={() => setFile(null)}
              className="ml-2 text-sm text-red-500 underline"
            >
              הסר קובץ
            </button>
          </div>
        )}

        {/* כפתור ייבוא */}
        <button
          onClick={handleImport}
          disabled={!file || isLoading}
          className="mt-6 w-full px-6 py-3 bg-primary hover:bg-primary disabled:bg-gray-600 rounded-lg transition-colors text-lg font-semibold"
        >
          {isLoading ? 'מייבא...' : 'ייבוא'}
        </button>
      </div>
      <div className="p-4">
        <h2 className="text-3xl text-white">מדריך ייצוא רשימת הצפייה שלכם</h2>
        <ol>
          <li>
            <strong className="text-xl">כניסה לאתר:</strong>
            <p>
              היכנסו לאחד מהאתרים AniList או MyAnimeList והתחברו לחשבונכם. לאחר
              מכן, גשו לפרופיל האישי שלכם.
            </p>
            <p>
              בכתובת ה-URL תוכלו לראות את שם המשתמש שלכם, כפי שמוצג בתמונה הבאה:
            </p>
            <img
              src="https://res.cloudinary.com/drejchdwh/image/upload/v1742227151/copyusername_efao0d.png"
              alt="העתקת שם משתמש"
              title="העתקת שם משתמש"
            />
          </li>
          <li>
            <strong className="text-xl">מעבר לאתר המיועד:</strong>
            <p>
              לחצו על הקישור הבא כדי לעבור לאתר המיועד:
              <a
                className="text-primary underline mr-1"
                href="https://malscraper.azurewebsites.net/"
                target="_blank"
              >
                למעבר לאתר לחצו כאן
              </a>
            </p>
          </li>
          <li>
            <strong className="text-xl">
              הזנת שם המשתמש ובחירת סוג הרשימה:
            </strong>
            <p>
              כאשר תגיעו לאתר, הזינו את שם המשתמש כפי שהופיע בכתובת ה-URL בשדה
              "Username".
            </p>
            <p>
              בחרו את סוג הרשימה המתאים – אם אתם משתמשים ב-MyAnimeList בחרו
              ב-MAL, ואם אתם משתמשים ב-AniList בחרו באופציה המתאימה.
            </p>
          </li>
          <li>
            <strong className="text-xl">הורדת הקובץ וייבוא:</strong>
            <p>
              לאחר בחירת סוג הרשימה, לחצו על הכפתור הכחול "SUBMIT". הקובץ יורד
              למחשבכם.
            </p>
            <p>
              העלו את הקובץ לאתר שלנו ולחצו על כפתור "ייבוא" כדי לייבא את רשימת
              הצפייה שלכם.
            </p>
            <img
              src="https://res.cloudinary.com/drejchdwh/image/upload/v1742227158/choosewebsite_bxyrf1.png"
              alt="בחירת האתר"
              title="בחירת האתר"
            />
          </li>
        </ol>
      </div>
    </>
  )
}
